// packages
import { z } from 'zod'
import { MessageProps } from './message'
import { privateRouteSchema } from './route'
import { ExamLibraryProps } from './exam-library'
import { PurchaseProps } from './purchase'
import { ConversationProps } from './conversation'
import { AnamneseProps } from './anamnese'
import { ExamConsultantProps } from './exam-consultant'

// enums
export enum user_role {
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR',
  CUSTOMER = 'CUSTOMER'
}

export enum formatted_user_role {
  ADMIN = 'Admin',
  MODERATOR = 'Moderador',
  CUSTOMER = 'Cliente'
}

export enum user_status {
  ALLOWED = 'ALLOWED',
  BLOCKED = 'BLOCKED',
  DELETED = 'DELETED'
}
export enum formatted_user_status {
  ALLOWED = 'Ativo',
  BLOCKED = 'Bloqueado',
  DELETED = 'Deletado'
}

// types
export type UserProps = {
  id?: number
  ref?: string
  userId?: number
  name?: string
  email?: string
  password?: string
  planDueDate?: string
  code?: string
  balance?: number
  planBalance?: number
  planId?: number
  role?: user_role
  status?: user_status
  createdAt?: Date
  updatedAt?: Date | null
  lastLoginAt?: Date

  // relationships
  messages?: MessageProps[]
  conversations?: ConversationProps[]
  anamnesis?: AnamneseProps[]
  examConsultants?: ExamConsultantProps[]
  examLibraries?: ExamLibraryProps[]
  purchases?: PurchaseProps[]
}

export type LoginSchema = z.infer<typeof loginSchema>
export type CreateUserSchema = z.infer<typeof createUserSchema>
export type RegisterUserSchema = z.infer<typeof registerUserSchema>
export type UpdateUserSchema = z.infer<typeof updateUserSchema>
export type UpdateUserStatusSchema = z.infer<typeof updateUserStatusSchema>
export type DeleteUserSchema = z.infer<typeof deleteUserSchema>
export type GetUsersSchema = z.infer<typeof getUsersSchema>
export type GetUserByEmailSchema = z.infer<typeof getUserByEmailSchema>
export type PasswordChangeSchema = z.infer<typeof passwordChangeSchema>
export type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>
export type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>

// schemas
export const loginSchema = z.object({
  email: z.string({ message: 'Informe o e-mail.' }).min(1, 'E-mail inválido.').max(100, 'Limite de caracteres: 100.'),
  password: z.string({ message: 'Informe a senha.' }).min(1, 'Senha inválida.').max(255, 'Limite de caracteres: 255.')
})

export const registerUserSchema = z
  .object({
    name: z.string({ message: 'Informe o nome.' }).min(1, 'Nome inválido.').max(100, 'Limite de caracteres: 100.'),
    email: z.string({ message: 'Informe o e-mail.' }).min(1, 'E-mail inválido.').max(100, 'Limite de caracteres: 100.'),
    password: z.string({ message: 'Informe a senha.' }).min(1, 'Senha inválida.').max(255, 'Limite de caracteres: 255.'),
    passwordConfirm: z.string({ message: 'Informe a confirmação de senha.' }).min(1, 'Confirmação de senha inválida.').max(255, 'Limite de caracteres: 255.').optional()
  })
  .refine(data => data.password === data.passwordConfirm, {
    message: 'As senhas não coincidem.',
    path: ['passwordConfirm']
  })
  .refine(data => data.password?.length >= 8, {
    message: 'As senhas não podem ser menores que 8 caracteres.',
    path: ['password']
  })

export const createUserSchema = privateRouteSchema.extend({
  name: z.string({ message: 'Informe o nome.' }).min(1, 'Nome inválido.').max(100, 'Limite de caracteres: 100.'),
  email: z.string({ message: 'Informe o e-mail.' }).min(1, 'E-mail inválido.').max(100, 'Limite de caracteres: 100.'),
  role: z.nativeEnum(user_role, { message: 'Função inválida.' }),
  planId: z.number({ message: 'Informe o plano.' }),
  password: z.string({ message: 'Informe a senha.' }).min(1, 'Senha inválida.').max(255, 'Limite de caracteres: 255.').optional()
})

export const updateUserSchema = createUserSchema.extend({
  ref: z.string({ message: 'Informe a referência.' }).uuid({ message: 'Referência inválida.' })
})

export const updateUserStatusSchema = privateRouteSchema.extend({
  ref: z.string({ message: 'Informe a referência.' }).uuid({ message: 'Referência inválida.' }),
  status: z.nativeEnum(user_status, { message: 'Status inválido.' })
})

export const getUsersSchema = privateRouteSchema

export const getUserByEmailSchema = z.object({
  email: z.string({ message: 'Informe o e-mail.' }).min(1, 'E-mail inválido.').max(100, 'Limite de caracteres: 100.')
})

export const deleteUserSchema = privateRouteSchema.extend({
  ref: z.string({ message: 'Informe a referência.' }).uuid({ message: 'Referência inválida.' })
})

export const passwordChangeSchema = privateRouteSchema
  .extend({
    password: z.string({ message: 'Informe a senha.' }).min(1, 'Senha inválida.').max(255, 'Limite de caracteres: 255.'),
    newPassword: z.string({ message: 'Informe a nova senha.' }).min(1, 'Nova senha inválida.').max(255, 'Limite de caracteres: 255.'),
    confirmPassword: z.string({ message: 'Informe a confirmação de senha.' }).min(1, 'Confirmação de senha inválida.').max(255, 'Limite de caracteres: 255.')
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'As senhas não coincidem.',
    path: ['confirmPassword']
  })

export const forgotPasswordSchema = z.object({
  email: z.string({ message: 'Informe o e-mail.' }).min(1, 'E-mail inválido.').max(100, 'Limite de caracteres: 100.')
})

export const resetPasswordSchema = z
  .object({
    code: z.string({ message: 'Informe o código.' }).min(1, 'Código inválido.').max(100, 'Limite de caracteres: 100.'),
    password: z.string({ message: 'Informe a senha.' }).min(1, 'Senha inválida.').max(255, 'Limite de caracteres: 255.'),
    confirmPassword: z.string({ message: 'Informe a confirmação de senha.' }).min(1, 'Confirmação de senha inválida.').max(255, 'Limite de caracteres: 255.')
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem.',
    path: ['confirmPassword']
  })

export class User {
  private props: UserProps

  get id() {
    return this.props.id
  }

  get ref() {
    return this.props.ref
  }

  get name() {
    return this.props.name
  }

  get email() {
    return this.props.email
  }

  get password() {
    return this.props.password
  }

  get planDueDate() {
    return this.props.planDueDate
  }

  get code() {
    return this.props.code
  }

  get balance() {
    return this.props.balance
  }

  get planBalance() {
    return this.props.planBalance
  }

  get planId() {
    return this.props.planId
  }

  get role() {
    return this.props.role
  }

  get status() {
    return this.props.status
  }

  get createdAt() {
    return this.props.createdAt
  }

  get updatedAt() {
    return this.props.updatedAt
  }

  get lastLoginAt() {
    return this.props.lastLoginAt
  }

  get messages() {
    return this.props.messages
  }

  get conversations() {
    return this.props.conversations
  }

  get anamnesis() {
    return this.props.anamnesis
  }

  get examConsultants() {
    return this.props.examConsultants
  }

  get examLibraries() {
    return this.props.examLibraries
  }

  get purchases() {
    return this.props.purchases
  }

  get totalSpentInPurchases() {
    return this.props.purchases?.reduce((acc, purchase) => acc + (purchase.value || 0), 0) || 0
  }

  get totalSpentInChat() {
    return this.props.messages?.reduce((acc, message) => acc + (message.spentValue || 0), 0) || 0
  }

  get totalSpentInExams() {
    return this.props.examConsultants?.reduce((acc, exam) => acc + (exam.spentValue || 0), 0) || 0
  }

  get currentBalance() {
    return (this.props.planBalance || 0) + (this.props.balance || 0)
  }

  constructor(props: UserProps) {
    this.props = props
  }
}
